/**
 * 一般的なオブジェクト
 */
class HashObject {
  [key: string]: any;
}

/**
 * Haoriの共通クラスです。
 */
class Haori {
  /** オプション */
  static options: HaoriOptions;

  /** 予約語のリスト */
  static reservedWords = [
    'await',
    'break',
    'case',
    'catch',
    'class',
    'const',
    'continue',
    'debugger',
    'default',
    'delete',
    'do',
    'else',
    'enum',
    'export',
    'extends',
    'false',
    'finally',
    'for',
    'function',
    'if',
    'import',
    'in',
    'instanceof',
    'new',
    'null',
    'return',
    'super',
    'switch',
    'this',
    'throw',
    'true',
    'try',
    'typeof',
    'var',
    'void',
    'while',
    'with',
    'yield',
    'let',
    'static',
    'implements',
    'interface',
    'package',
    'private',
    'protected',
    'public',
    'enum',
  ];

  /**
   * アラートダイアログを表示します。
   *
   * @param message 表示するメッセージ
   * @return アラートダイアログ表示後のプロミス
   */
  static alert(message: string) {
    return this.options.alertProcess(message);
  }

  /**
   * メッセージを表示します。
   *
   * @param message 表示するメッセージ
   * @param level メッセージの表示レベル
   * @return アラートダイアログ表示後のプロミス
   */
  static message(message: string, level: HaoriMessageLevel) {
    return this.options.messageProcess(message, level);
  }

  /**
   * 確認ダイアログを表示します。
   *
   * @param message 表示するメッセージ
   * @return アラートダイアログ表示後のプロミス（戻り値はクリックされたボタン）
   */
  static confirm(message: string) {
    return this.options.confirmProcess(message);
  }

  /**
   * ダイアログ検出処理です。セレクタに応じたダイアログを検出します。
   * セレクタが空文字の場合は対象エレメントの先祖エレメントを検索します。
   *
   * @param target 対象ダイアログ
   * @param selector セレクタ
   */
  static findDialog(target: Element, selector: string) {
    return this.options.findDialog(target, selector);
  }

  /**
   * モーダルダイアログを表示します。
   *
   * @param element ダイアログエレメント
   */
  static openDialog(element: Element) {
    this.options.openDialog(element);
  }

  /**
   * モーダルダイアログを閉じます。
   *
   * @param element ダイアログエレメント
   */
  static closeDialog(element: Element) {
    this.options.closeDialog(element);
  }

  /**
   * objectのnameパラメータの値を取得します。nameにドットがある場合は入れ子のオブジェクトとして扱います。
   *
   * @param object オブジェクト
   * @param name パラメータ名
   * @returns 値
   */
  static getValue(object: HashObject, name: string | null): any {
    if (object == undefined || name == undefined || name == null) {
      return undefined;
    }
    let point = name.indexOf('.');
    if (point > 0) {
      let childName = name.substring(point + 1);
      let value = object[name.substring(0, point)];
      if (Array.isArray(value)) {
        let array: HashObject = [];
        value.forEach((child) => {
          array.push(this.getValue(child, childName));
        });
        return array;
      } else {
        return this.getValue(value, childName);
      }
    }
    return object[name];
  }

  /** 変数名として使える名前のパターン（Unicodeの識別子の文字とドル記号） */
  private static IDENTIFIER_PATTERN = new RegExp('^[\\p{ID_Start}$_][\\p{ID_Continue}$]*$', 'u');

  /**
   * 変数名として使える名前かを判定します（識別子の文字・アンダースコア・ドル記号で、数字で始まらず、予約語でないもの）。
   *
   * @param name 名前
   * @return 変数名として使える場合はtrue
   */
  static isIdentifier(name: string): boolean {
    return this.IDENTIFIER_PATTERN.test(name) && this.reservedWords.indexOf(name) < 0;
  }

  /**
   * 文字列をスクリプトに埋め込める文字列リテラルに変換します。
   *
   * @param text 文字列
   * @return 文字列リテラル
   */
  private static toStringLiteral(text: string): string {
    return JSON.stringify(text).replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029');
  }

  /**
   * テンプレートとして登録します。
   * Haoriは、ページ読み込み時のHTMLとHaoriが複製・展開したテンプレートの{{}}だけをスクリプトとして実行し、
   * 後から挿入された文字やアトリビュートはデータとしてそのまま表示します。
   * アプリケーションのスクリプトで{{}}を含むHTMLやアトリビュートを後から設定した場合に、設定したノードをこの関数で登録してください。
   * 登録するのは、まだ登録していないテキストとアトリビュートと、Haori以外が変更したテキストとアトリビュートです。
   * 登録済みのテンプレート（評価済みの値）とデータとして挿入したノードは変更しません。
   * データ（利用者の入力値やサーバーから取得した値）を含むノードは登録しないでください（値の中の{{}}がスクリプトとして実行されます）。
   *
   * @param node 対象ノード（子孫ノードも登録します）
   */
  static registerTemplate(node: Node) {
    HaoriBind.flushMutations();
    HaoriBind.registerTemplate(node, true);
    let element = node.nodeType == Node.ELEMENT_NODE ? <Element>node : node.parentElement;
    if (element != null && element.closest('html') != null) {
      HaoriBind.bind(element);
    }
  }

  /**
   * データとして記録します。記録したノード以下はテンプレートとして登録せず、{{}}を含んでいてもスクリプトとして実行しません。
   * ページの読み込み中（DOMContentLoadedより前）にアプリケーションのスクリプトでデータを挿入する場合は、
   * 読み込み時のHTMLと区別できないため、挿入する前にこの関数で記録してください。
   *
   * @param node 対象ノード（子孫ノードも記録します）
   */
  static markAsData(node: Node) {
    HaoriBind.markAsData(node);
  }

  /**
   * スクリプトを実行します。
   *
   * @param params 変数として設定するパラメータ
   * @param script 実行するスクリプト
   * @param element 対象エレメント（エラー時のログ用）
   * @returns 実行結果
   */
  static executeScript(params: any, script: string, element: Node | null = null): any {
    let func = ``;
    let nullExists: string[] = [];
    let objectExists: string[] = [];
    script.split(/[\s\[\]\,]/g).forEach((scriptPart) => {
      // 変数のデフォルト値設定
      if (scriptPart.indexOf('(') == 0) {
        scriptPart = scriptPart.substring(1); // 引数の場合は"("で始まるため括弧を除去する
      }
      if (scriptPart.indexOf(')') == scriptPart.length - 1) {
        scriptPart = scriptPart.substring(0, scriptPart.length - 1); // 引数の場合は")"で始まるため括弧を除去する
      }
      if (scriptPart.indexOf('!') == 0) {
        scriptPart = scriptPart.substring(1); // 否定の!を除去する
      }
      if (!scriptPart.match(/^!?[a-zA-Z][_a-zA-Z0-9\\.]*$/) || scriptPart.match(/^[0-9]+$/)) {
        return;
      }
      if (this.reservedWords.indexOf(scriptPart) >= 0) {
        return;
      }
      let parts = scriptPart.split('.');
      if (parts.length == 1) {
        let key = scriptPart.indexOf('!') === 0 ? scriptPart.substring(1) : scriptPart;
        if (nullExists.indexOf(key) < 0) {
          func += `var ${key} = null;\n`;
          nullExists.push(key);
        }
      } else {
        let key = '';
        for (let i = 0; i < parts.length - 1; i++) {
          if (i > 0) {
            key += '.';
          }
          key += parts[i];
          if (i == 0) {
            let subKey = key.indexOf('!') === 0 ? key.substring(1) : key;
            if (objectExists.indexOf(subKey) < 0) {
              func += `var ${subKey} = {};\n`;
              nullExists.push(subKey);
              objectExists.push(subKey);
            }
          } else {
            if (objectExists.indexOf(key) < 0) {
              func += `${key} = {};\n`;
              nullExists.push(key);
              objectExists.push(key);
            }
          }
        }
        if (nullExists.indexOf(scriptPart) < 0) {
          scriptPart = scriptPart.indexOf('!') === 0 ? scriptPart.substring(1) : scriptPart;
          func += `${scriptPart} = null;\n`;
          nullExists.push(scriptPart);
        }
      }
    });
    if (params != null) {
      for (let key in params) {
        let value = params[key];
        key = key.replace('[]', '');
        if (!this.isIdentifier(key)) {
          // キー名はスクリプトにそのまま組み込むため、変数名として使えないキー（データ由来のキーなど）は無視する
          console.warn('変数名として使えないキーは無視します', key, element);
          continue;
        }
        switch (typeof value) {
          case 'number':
          case 'boolean':
            func += `var ${key} = ${value};\n`;
            break;
          default:
            // 値はJSONの文字列リテラルとして埋め込み、JSON.parseで復元する
            // （文字列の区切りやエスケープを差し込まれないようにし、__proto__などのキーもデータとして扱う）
            let json = value === undefined ? undefined : JSON.stringify(value);
            if (json === undefined) {
              func += `var ${key} = null;\n`;
            } else {
              func += `var ${key} = JSON.parse(${this.toStringLiteral(json)});\n`;
            }
        }
      }
    }
    func += `return (${script});\n`;
    try {
      return Function(func)();
    } catch (reason) {
      console.error('スクリプトの実行でエラーが発生しました\n', element, '\n' + func, 'Reason: ' + reason);
      throw reason;
    }
  }

  /**
   * 通信を行います。
   *
   * @param element 処理対象のエレメント
   * @param url URL
   * @param request リクエスト
   * @param callback 正常完了時のコールバック関数（レスポンス, データ）
   * @param error エラー発生時のコールバック関数（原因）
   */
  static fetch(
    elements: Element[],
    url: string,
    request: RequestInit = {},
    callback: (response: Response, data: any) => void = () => {},
    error: (reason: any) => void = () => {}
  ) {
    if (typeof request.mode == 'undefined') {
      request.mode = 'cors';
    }
    if (typeof request.credentials == 'undefined') {
      request.credentials = 'same-origin';
    }
    url = Haori.options.requestConverter(url, request);
    let lastResponse: Response | null = null;
    fetch(url, request)
      .then((srcResponse) => {
        Haori.options
          .responseConverter(url, request, srcResponse)
          .then((response) => {
            elements.forEach((element) => {
              HaoriBind.setDataAttribute(element, 'data-fetch-status', response.status.toString());
            });
            lastResponse = response;
            let contentType = response.headers.get('Content-Type') || '';
            if (response.headers.get('Content-Length') == '0') {
              return response.text();
            } else if (contentType.indexOf('application/json') == 0) {
              return response.json();
            } else if (contentType.indexOf('text/') == 0 || contentType.indexOf('/xml') > 0 || contentType == '') {
              return response.text();
            } else {
              return response.blob();
            }
          })
          .then((result) => {
            callback(<Response>lastResponse, result);
            elements.forEach((element) => {
              element.dispatchEvent(
                new CustomEvent('fetch', {
                  bubbles: true,
                  cancelable: true,
                  composed: true,
                  detail: {
                    request: request,
                    response: {
                      ok: lastResponse?.ok,
                      status: lastResponse?.status,
                      body: result,
                    },
                  },
                })
              );
            });
          });
      })
      .catch((reason) => {
        console.error(reason);
        error(reason);
        throw reason;
      });
  }

  /**
   * 対象エレメントで指定した属性を持っているエレメントに対して操作を行います。
   * 対象が見つからない場合は処理を実行しません。
   *
   * @param element 対象エレメント
   * @param attribute 属性
   * @param callback 対象エレメントに対する操作（対象エレメント, 属性値）
   * @returns 操作関数の戻り値（対象が見つからない場合はnull）
   */
  static hasAttribute(
    element: Element,
    attribute: string,
    callback: (element: Element, value: string) => any = () => {
      return true;
    }
  ) {
    let value = element.getAttribute(attribute);
    if (value == null) {
      return null;
    }
    return callback(element, value || '');
  }

  /**
   * 対象エレメントもしくはその祖先エレメントで指定した属性を持っているエレメントに対して操作を行います。
   * 対象が見つからない場合は処理を実行しません。
   *
   * @param element 対象エレメント
   * @param attribute 属性
   * @param callback 対象エレメントに対する操作（対象エレメント, 属性値）
   * @returns 操作関数の戻り値（対象が見つからない場合はnull）
   */
  static hasClosestAttribute(
    element: Element,
    attribute: string,
    callback: (element: Element, value: string) => any = () => {
      return true;
    }
  ) {
    let value = element.getAttribute(attribute);
    let target: Element | null = null;
    if (value == null) {
      target = element.closest(`[${attribute}]`);
      if (target != null) {
        value = target.getAttribute(attribute);
      }
    } else {
      target = element;
    }
    if (target == null) {
      return null;
    } else {
      return callback(target, value || '');
    }
  }

  /**
   * セレクタに一致するエレメントを取得します。存在しない場合はログにエラーメッセージを出力します。
   *
   * @param selector セレクタ
   * @returns エレメント
   */
  static querySelector(selector: string): Element | null {
    let element = document.querySelector(selector);
    if (element == null) {
      console.error('「' + selector + '」に相当するエレメントが存在しません');
    }
    return element;
  }

  /**
   * セレクタに一致するエレメントを取得します。存在しない場合はログにエラーメッセージを出力します。
   *
   * @param selector セレクタ
   * @returns エレメントリスト
   */
  static querySelectorAll(selector: string): NodeListOf<Element> {
    let elements = document.querySelectorAll(selector);
    if (elements == null || elements.length == 0) {
      console.error('「' + selector + '」に相当するエレメントが存在しません');
    }
    return elements;
  }

  /**
   * 対象エレメントの前にエレメントを挿入します。
   *
   * @param insert 挿入エレメント
   * @param target 対象エレメント
   */
  static insertBefore(insert: Element, target: Element) {
    let parent = target.parentElement;
    if (parent == null) {
      console.error('親エレメントがありません', target);
      return;
    }
    parent.insertBefore(insert, target);
  }

  /**
   * 対象エレメントの後ろにエレメントを挿入します。
   *
   * @param insert 挿入エレメント
   * @param target 対象エレメント
   */
  static insertAfter(insert: Element, target: Element) {
    let parent = target.parentElement;
    if (parent == null) {
      console.error('親エレメントがありません', target);
      return;
    }
    let next = target.nextElementSibling;
    if (next == null) {
      parent.appendChild(insert);
    } else {
      parent.insertBefore(insert, next);
    }
  }
}

/**
 * オプション設定を行うクラスです。
 */
class HaoriOptions {
  /**
   * アラートダイアログを表示します。
   *
   * @param message 表示するメッセージ
   * @return アラートダイアログ表示後のプロミス
   */
  alertProcess: (message: string) => Promise<void>;

  /**
   * 確認ダイアログを表示します。
   *
   * @param message 表示するメッセージ
   * @return アラートダイアログ表示後のプロミス（戻り値はクリックされたボタン）
   */
  confirmProcess: (message: string) => Promise<boolean>;

  /**
   * メッセージを表示します。
   *
   * @param message 表示するメッセージ
   * @param level メッセージの表示レベル
   * @return アラートダイアログ表示後のプロミス
   */
  messageProcess: (message: string, level: HaoriMessageLevel) => Promise<void>;

  /**
   * AJAX通信のリクエストコンバータです。
   *
   * @param url アクセス先URL
   * @param request リクエスト
   * @return 変更されたアクセス先URL
   */
  requestConverter: (url: string, request: RequestInit) => string;

  /**
   * AJAX通信のレスポンスコンバータです。
   *
   * @param url アクセス先URL
   * @param request リクエスト
   * @param respnse レスポンス
   * @return 変換されたレスポンスのプロミス
   */
  responseConverter: (url: string, request: RequestInit, response: Response) => Promise<Response>;

  /**
   * エラーメッセージコンバータです。
   *
   * @param url アクセス先URL
   * @paran request リクエスト
   * @param response レスポンス
   * @param message メッセージ
   * @return 変換されたメッセージ
   */
  errorMessageConverter: (url: string, request: RequestInit, response: Response, message: any) => any;

  /**
   * ダイアログ検出処理です。セレクタに応じたダイアログを検出します。
   * セレクタがnullもしくは空文字の場合は対象エレメントの先祖エレメントを検索します。
   *
   * @param target 対象ダイアログ
   * @param selector セレクタ
   */
  findDialog: (target: Element, selector: string) => Element | null;

  /**
   * ダイアログを開きます。
   *
   * @param element ダイアログ
   */
  openDialog: (element: Element) => void;

  /**
   * ダイアログを閉じます。
   *
   * @param element ダイアログ
   */
  closeDialog: (element: Element) => void;

  /**
   * ストレージキャッシュを使用するか。
   *
   * @return 使用する場合はtrue
   */
  useStorageCache: () => boolean;

  constructor() {
    this.alertProcess = async (message) => {
      alert(message);
    };
    this.confirmProcess = async (message) => {
      return confirm(message);
    };
    this.messageProcess = async (message, level) => {
      let text = '';
      switch (level) {
        case HaoriMessageLevel.SUCCESS:
          text = 'SUCCESS';
          break;
        case HaoriMessageLevel.INFO:
          text = 'INFO';
          break;
        case HaoriMessageLevel.WARNING:
          text = 'WARNING';
          break;
        case HaoriMessageLevel.DANGER:
          text = 'DANGER';
          break;
      }
      alert(text + ': ' + message);
    };
    this.requestConverter = (url, request) => {
      return url;
    };
    this.responseConverter = async (url, request, response) => {
      return response;
    };
    this.errorMessageConverter = (url, request, response, message) => {
      return message;
    };
    this.findDialog = (target, selector) => {
      if (selector == null || selector == '') {
        return target.closest('dialog');
      } else {
        return document.querySelector(selector);
      }
    };
    this.openDialog = (element) => {
      (<HTMLDialogElement>element).showModal();
    };
    this.closeDialog = (element) => {
      (<HTMLDialogElement>element).close();
    };
    this.useStorageCache = () => {
      return true;
    };
  }
}
Haori.options = new HaoriOptions();

/**
 * メッセージの表示レベル。
 */
enum HaoriMessageLevel {
  SUCCESS = 1,

  INFO = 2,

  WARNING = 3,

  DANGER = 4,
}

// スクリプトに渡す関数を格納したオブジェクト
declare var Funcs: any;
